import ConfirmationModal from "@/modules/common/components/modals/cofirmation-modal";
import StyledTextFieldWithTitle from "@/modules/common/components/input/styled-text-field-with-title";
import CommonIconButton from "@/modules/common/components/ui-kit/buttons/icon-button";
import { todayKey } from "@/modules/property-tracks/utils/listing";
import { Box, Button, FormHelperText, Typography } from "@mui/material";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Control, Controller, useFieldArray, useWatch } from "react-hook-form";

// Overlap and past-date rules live in HospitalityListingSchema.
export default function ExceptionalPrices({ control }: { control: Control<any> }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "exceptionalPrices",
  });
  const ranges = useWatch({ control, name: "exceptionalPrices" }) ?? [];
  const [isOpen, setIsOpen] = useState(fields.length > 0);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);
  const today = todayKey();

  return (
    <Box
      sx={(t) => ({
        display: "flex",
        flexDirection: "column",
        gap: t.nawy.spacing.xs,
      })}
    >
      <Box>
        <Button
          size="small"
          startIcon={<Plus size={16} />}
          onClick={() => setIsOpen((open) => !open)}
        >
          Exceptional Dates
        </Button>
      </Box>

      {isOpen && (
        <>
          <Typography
            sx={(t) => ({
              ...t.nawy.typography.english.variants.body.sm.regular,
              color: t.nawy.colors.semantic.text.secondary,
            })}
          >
            Dates outside these ranges use the base price.
          </Typography>

          {fields.map((field, index) => (
            <Box
              key={field.id}
              sx={(t) => ({
                display: "flex",
                alignItems: "flex-start",
                gap: t.nawy.spacing.xs,
              })}
            >
              <Controller
                control={control}
                name={`exceptionalPrices.${index}.fromDate`}
                render={({ field: dateField, fieldState }) => (
                  <StyledTextFieldWithTitle
                    {...dateField}
                    type="date"
                    title="From *"
                    fullWidth
                    size="small"
                    inputProps={{ min: today }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name={`exceptionalPrices.${index}.toDate`}
                render={({ field: dateField, fieldState }) => (
                  <StyledTextFieldWithTitle
                    {...dateField}
                    type="date"
                    title="To *"
                    fullWidth
                    size="small"
                    inputProps={{ min: ranges[index]?.fromDate || today }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name={`exceptionalPrices.${index}.price`}
                render={({ field: priceField, fieldState }) => (
                  <StyledTextFieldWithTitle
                    {...priceField}
                    formatNumeric
                    title="Price *"
                    placeholder="Price"
                    fullWidth
                    size="small"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <Box sx={{ pt: 3.25 }}>
                <CommonIconButton
                  aria-label="Delete exceptional date range"
                  onClick={() => setPendingDeleteIndex(index)}
                >
                  <Trash2 size={16} />
                </CommonIconButton>
              </Box>
            </Box>
          ))}

          <Box>
            <Button
              size="small"
              startIcon={<Plus size={16} />}
              onClick={() => append({ fromDate: "", toDate: "", price: "" })}
            >
              Add Exceptional Dates
            </Button>
          </Box>

          <Controller
            control={control}
            name="exceptionalPrices"
            render={({ fieldState }) =>
              fieldState.error?.root?.message || fieldState.error?.message ? (
                <FormHelperText error>
                  {fieldState.error?.root?.message || fieldState.error?.message}
                </FormHelperText>
              ) : (
                <></>
              )
            }
          />
        </>
      )}

      <ConfirmationModal
        open={pendingDeleteIndex !== null}
        setOpen={(open: boolean) => !open && setPendingDeleteIndex(null)}
        title="Delete exceptional dates?"
        body="These dates will go back to the base price once the listing is saved."
        onConfirmHandler={() => {
          if (pendingDeleteIndex !== null) remove(pendingDeleteIndex);
          setPendingDeleteIndex(null);
        }}
        onCancelHandler={() => setPendingDeleteIndex(null)}
      />
    </Box>
  );
}
